import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import clsx from 'clsx';
import {
  History,
  KeyRound,
  Link as LinkIcon,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Drawer } from '../components/ui/Drawer';
import { Skeleton } from '../components/ui/Skeleton';
import { TextField } from '../components/ui/Field';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { AddRelationModal } from '../components/shared/AddRelationModal';
import { EntityPicker, type PickedEntity } from '../components/shared/EntityPicker';
import {
  HouseholdDrawer,
  type HouseholdDrawerMode,
} from '../components/people/HouseholdDrawer';
import type {
  HouseholdRecord,
  StudentRecord,
} from '../components/people/types';

export const Route = createFileRoute('/_authed/people/households/$householdId')({
  component: HouseholdDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes
//
// The enriched GET /api/households/:id is expected (per plan §4.4) to embed
// members[], parentUser, and the per-link `isPrimary`/`relation` columns.
// Treat them all as optional so the page renders correctly even before the
// backend agent's PR lands.
// ---------------------------------------------------------------------------

interface HouseholdMember {
  id: string;
  householdId: string;
  name: string;
  relation: string;
  phone: string;
  email: string | null;
  isPrimary: boolean;
  createdAt: string;
}

interface ParentUserEmbed {
  id: string;
  email: string;
  displayName: string;
  status: 'active' | 'inactive' | 'pending';
  lastLoginAt?: string | null;
}

interface LinkedStudent extends StudentRecord {
  relation?: string | null;
  isPrimary?: boolean;
}

interface HouseholdDetail extends HouseholdRecord {
  students: LinkedStudent[];
  members?: HouseholdMember[];
  parentUser?: ParentUserEmbed | null;
}

type Tab = 'students' | 'members' | 'parent' | 'activity';

const TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof Users }> = [
  { value: 'students', label: 'Students', icon: Users },
  { value: 'members', label: 'Members', icon: Users },
  { value: 'parent', label: 'Parent account', icon: KeyRound },
  { value: 'activity', label: 'Activity', icon: History },
];

function HouseholdDetailPage(): JSX.Element {
  const { householdId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage = role === 'super_admin' || role === 'academic_admin';

  const [tab, setTab] = useState<Tab>('students');
  const [editDrawer, setEditDrawer] = useState<HouseholdDrawerMode | null>(null);
  const [linkStudentOpen, setLinkStudentOpen] = useState(false);
  const [memberDrawer, setMemberDrawer] = useState<MemberDrawerState>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const householdQuery = useQuery({
    queryKey: ['household', 'detail', householdId] as const,
    queryFn: ({ signal }) =>
      apiRequest<HouseholdDetail>(
        `/api/households/${encodeURIComponent(householdId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const household = householdQuery.data;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['household', 'detail', householdId] });
    void queryClient.invalidateQueries({ queryKey: ['households'] });
  };

  const reinviteMutation = useMutation({
    mutationFn: (userId: string) =>
      apiRequest<{ ok: true; activationUrl: string }>(
        `/api/users/${encodeURIComponent(userId)}/reinvite`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Activation email re-sent.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not resend invitation.', {
        variant: 'error',
      });
    },
  });

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!household) return [];
    const memberCount = household.members?.length ?? 0;
    const portal = household.parentUser?.status
      ? household.parentUser.status === 'active'
        ? 'Active'
        : household.parentUser.status === 'pending'
          ? 'Pending'
          : 'Inactive'
      : 'Not invited';
    return [
      {
        label: 'Linked students',
        value: household.students.length,
        onClick: () => setTab('students'),
      },
      {
        label: 'Members',
        value: memberCount,
        onClick: () => setTab('members'),
      },
      {
        label: 'Portal',
        value: portal,
        onClick: () => setTab('parent'),
      },
      {
        label: 'Last login',
        value: household.parentUser?.lastLoginAt
          ? formatDate(household.parentUser.lastLoginAt, { withTime: true })
          : '—',
      },
    ];
  }, [household]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={householdQuery.error}
        title="Family"
        onRetry={() => householdQuery.refetch()}
      >
        {householdQuery.isLoading || !household ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={`${household.primaryContactName}'s family`}
              sublabel={household.primaryPhone}
              statusChip={<PortalStatusChip user={household.parentUser ?? null} />}
              backTo="/people/households"
              actions={
                canManage ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditDrawer({ kind: 'edit', id: household.id })}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      icon={<LinkIcon size={14} aria-hidden="true" />}
                      onClick={() => setLinkStudentOpen(true)}
                    >
                      Link student
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<UserPlus size={14} aria-hidden="true" />}
                      onClick={() => setMemberDrawer({ kind: 'create' })}
                    >
                      Add member
                    </Button>
                    {household.parentUser?.status === 'pending' ? (
                      <OverflowMenu
                        open={overflowOpen}
                        onOpenChange={setOverflowOpen}
                        pending={reinviteMutation.isPending}
                        onResend={() => {
                          setOverflowOpen(false);
                          if (household.parentUser) {
                            reinviteMutation.mutate(household.parentUser.id);
                          }
                        }}
                      />
                    ) : null}
                  </>
                ) : null
              }
            />

            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="Family sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map(({ value, label, icon: Icon }) => {
                const active = tab === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>

            {tab === 'students' ? (
              <StudentsTab
                household={household}
                canManage={canManage}
                onLinkOpen={() => setLinkStudentOpen(true)}
                onChanged={invalidate}
              />
            ) : null}
            {tab === 'members' ? (
              <MembersTab
                household={household}
                canManage={canManage}
                onAdd={() => setMemberDrawer({ kind: 'create' })}
                onEdit={(member) => setMemberDrawer({ kind: 'edit', member })}
                onChanged={invalidate}
              />
            ) : null}
            {tab === 'parent' ? (
              <ParentTab
                household={household}
                canManage={canManage}
                reinvitePending={reinviteMutation.isPending}
                onResend={() => {
                  if (household.parentUser) {
                    reinviteMutation.mutate(household.parentUser.id);
                  }
                }}
              />
            ) : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="household"
                targetId={household.id}
                title="Activity"
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      {canManage && household ? (
        <HouseholdDrawer
          mode={editDrawer}
          onClose={() => setEditDrawer(null)}
          initial={household}
          isLoadingInitial={false}
          loadError={null}
          onCreated={() => {
            setEditDrawer(null);
            invalidate();
          }}
          onUpdated={() => {
            setEditDrawer(null);
            invalidate();
            toast('Family saved.', { variant: 'success' });
          }}
        />
      ) : null}

      {canManage && household ? (
        <LinkStudentsModal
          open={linkStudentOpen}
          onClose={() => setLinkStudentOpen(false)}
          householdId={household.id}
          existingStudentIds={household.students.map((s) => s.id)}
          onSuccess={() => {
            invalidate();
            toast('Students linked.', { variant: 'success' });
          }}
        />
      ) : null}

      {canManage && household ? (
        <MemberDrawer
          state={memberDrawer}
          householdId={household.id}
          onClose={() => setMemberDrawer(null)}
          onSaved={() => {
            setMemberDrawer(null);
            invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Portal status chip
// ---------------------------------------------------------------------------

function PortalStatusChip({
  user,
}: {
  user: ParentUserEmbed | null;
}): JSX.Element {
  if (!user) {
    return (
      <span className="inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--color-text-muted)]">
        Portal not invited
      </span>
    );
  }
  const tone =
    user.status === 'active'
      ? 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]'
      : user.status === 'pending'
        ? 'bg-[color:var(--color-warning)]/15 text-[color:var(--color-warning)]'
        : 'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]';
  const label =
    user.status === 'active'
      ? 'Portal active'
      : user.status === 'pending'
        ? 'Portal pending'
        : 'Portal inactive';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
        tone,
      )}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Header overflow menu
// ---------------------------------------------------------------------------

interface OverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  pending: boolean;
  onResend: () => void;
}

function OverflowMenu({
  open,
  onOpenChange,
  pending,
  onResend,
}: OverflowMenuProps): JSX.Element {
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        loading={pending}
      >
        More
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 min-w-[180px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
        >
          <button
            role="menuitem"
            type="button"
            onClick={onResend}
            className="block w-full px-3 py-2 text-left text-sm text-[color:var(--color-text)] hover:bg-[color:var(--color-surface)] focus:outline-none"
          >
            Resend activation
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Students tab
// ---------------------------------------------------------------------------

interface StudentsTabProps {
  household: HouseholdDetail;
  canManage: boolean;
  onLinkOpen: () => void;
  onChanged: () => void;
}

function StudentsTab({
  household,
  canManage,
  onLinkOpen,
  onChanged,
}: StudentsTabProps): JSX.Element {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handleUnlink = async (studentId: string, name: string): Promise<void> => {
    const ok = await confirm({
      title: 'Unlink student?',
      description: `${name} will no longer be linked to this family.`,
      confirmLabel: 'Unlink',
      variant: 'danger',
    });
    if (!ok) return;
    setPendingId(studentId);
    try {
      await apiRequest<{ ok: true }>(
        `/api/households/${encodeURIComponent(household.id)}/students/${encodeURIComponent(studentId)}`,
        { method: 'DELETE' },
      );
      toast('Student unlinked.', { variant: 'success' });
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not unlink student.', {
        variant: 'error',
      });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <RelationPanel
      title="Students"
      count={household.students.length}
      description="Students belonging to this family."
      action={
        canManage ? (
          <Button
            size="sm"
            icon={<LinkIcon size={14} aria-hidden="true" />}
            onClick={onLinkOpen}
          >
            Link student
          </Button>
        ) : null
      }
      isEmpty={household.students.length === 0}
      empty={{
        copy: 'No students linked yet. Use Link student to add one.',
        cta: canManage ? (
          <Button
            size="sm"
            icon={<LinkIcon size={14} aria-hidden="true" />}
            onClick={onLinkOpen}
          >
            Link student
          </Button>
        ) : undefined,
      }}
    >
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            <tr>
              <th className="px-4 py-2">Student</th>
              <th className="px-4 py-2">Admission #</th>
              <th className="px-4 py-2">Relation</th>
              <th className="px-4 py-2 text-right">Open</th>
              {canManage ? <th className="px-4 py-2 text-right">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {household.students.map((s) => (
              <tr
                key={s.id}
                className="border-t border-[color:var(--color-border)] hover:bg-[color:var(--color-surface)]"
              >
                <td className="px-4 py-2 align-middle font-medium text-[color:var(--color-text)]">
                  <Link
                    to="/people/students/$studentId"
                    params={{ studentId: s.id }}
                    className="hover:text-gold-700"
                  >
                    {s.name}
                  </Link>
                  {s.isPrimary ? (
                    <span className="ml-2 inline-flex items-center rounded-full bg-gold-50 px-1.5 py-0.5 text-[10px] font-semibold text-gold-700 dark:bg-gold-700/20">
                      Primary
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2 align-middle font-mono text-xs text-[color:var(--color-text-muted)]">
                  {s.admissionNumber}
                </td>
                <td className="px-4 py-2 align-middle text-xs capitalize text-[color:var(--color-text-muted)]">
                  {s.relation ?? 'child'}
                </td>
                <td className="px-4 py-2 align-middle text-right">
                  <Link
                    to="/people/students/$studentId"
                    params={{ studentId: s.id }}
                    className="text-xs font-medium text-gold-700 hover:underline"
                  >
                    Open
                  </Link>
                </td>
                {canManage ? (
                  <td className="px-4 py-2 align-middle text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={pendingId === s.id}
                      onClick={() => {
                        void handleUnlink(s.id, s.name);
                      }}
                    >
                      Unlink
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

interface LinkStudentsModalProps {
  open: boolean;
  onClose: () => void;
  householdId: string;
  existingStudentIds: string[];
  onSuccess: () => void;
}

function LinkStudentsModal({
  open,
  onClose,
  householdId,
  existingStudentIds,
  onSuccess,
}: LinkStudentsModalProps): JSX.Element {
  const [picked, setPicked] = useState<PickedEntity[]>([]);

  const submit = async (): Promise<{ ok: true; linked: number }> => {
    if (picked.length === 0) {
      throw new ApiError(400, 'invalid', 'Select at least one student.');
    }
    const studentIds = picked
      .map((p) => p.id)
      .filter((id) => !existingStudentIds.includes(id));
    if (studentIds.length === 0) {
      throw new ApiError(400, 'invalid', 'All selected students are already linked.');
    }
    return apiRequest<{ ok: true; linked: number }>(
      `/api/households/${encodeURIComponent(householdId)}/students`,
      { method: 'POST', body: { studentIds } },
    );
  };

  return (
    <AddRelationModal<{ ok: true; linked: number }>
      open={open}
      onClose={() => {
        setPicked([]);
        onClose();
      }}
      title="Link students to this family"
      description="Pick one or more students who belong to this family."
      picker={
        <EntityPicker
          entity="student"
          multi
          value={picked}
          onChange={(v) => {
            if (Array.isArray(v)) setPicked(v);
            else setPicked(v.id ? [v] : []);
          }}
        />
      }
      submitLabel="Link"
      canSubmit={picked.length > 0}
      onSubmit={submit}
      onSuccess={onSuccess}
    />
  );
}

// ---------------------------------------------------------------------------
// Members tab
// ---------------------------------------------------------------------------

interface MembersTabProps {
  household: HouseholdDetail;
  canManage: boolean;
  onAdd: () => void;
  onEdit: (member: HouseholdMember) => void;
  onChanged: () => void;
}

function MembersTab({
  household,
  canManage,
  onAdd,
  onEdit,
  onChanged,
}: MembersTabProps): JSX.Element {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const members = household.members ?? [];

  const handleDelete = async (member: HouseholdMember): Promise<void> => {
    const ok = await confirm({
      title: 'Remove family member?',
      description: `${member.name} will be removed from this family.`,
      confirmLabel: 'Remove',
      variant: 'danger',
    });
    if (!ok) return;
    setPendingId(member.id);
    try {
      await apiRequest<{ ok: true }>(
        `/api/households/${encodeURIComponent(household.id)}/members/${encodeURIComponent(member.id)}`,
        { method: 'DELETE' },
      );
      toast('Member removed.', { variant: 'success' });
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not remove member.', {
        variant: 'error',
      });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <RelationPanel
      title="Members"
      count={members.length}
      description="People in this family. The main contact comes from the family’s own details."
      action={
        canManage ? (
          <Button
            size="sm"
            icon={<Plus size={14} aria-hidden="true" />}
            onClick={onAdd}
          >
            Add member
          </Button>
        ) : null
      }
      isEmpty={members.length === 0}
      empty={{
        copy: 'No additional members recorded yet.',
        cta: canManage ? (
          <Button
            size="sm"
            icon={<Plus size={14} aria-hidden="true" />}
            onClick={onAdd}
          >
            Add member
          </Button>
        ) : undefined,
      }}
    >
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Relation</th>
              <th className="px-4 py-2">Phone</th>
              <th className="px-4 py-2">Email</th>
              {canManage ? <th className="px-4 py-2 text-right">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr
                key={m.id}
                className="border-t border-[color:var(--color-border)] hover:bg-[color:var(--color-surface)]"
              >
                <td className="px-4 py-2 align-middle font-medium text-[color:var(--color-text)]">
                  {m.name}
                  {m.isPrimary ? (
                    <span className="ml-2 inline-flex items-center rounded-full bg-gold-50 px-1.5 py-0.5 text-[10px] font-semibold text-gold-700 dark:bg-gold-700/20">
                      Primary
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2 align-middle text-xs capitalize text-[color:var(--color-text-muted)]">
                  {m.relation}
                </td>
                <td className="px-4 py-2 align-middle text-[color:var(--color-text-muted)]">
                  {m.phone}
                </td>
                <td className="px-4 py-2 align-middle text-[color:var(--color-text-muted)]">
                  {m.email ?? '—'}
                </td>
                {canManage ? (
                  <td className="px-4 py-2 align-middle">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onEdit(m)}
                        aria-label={`Edit ${m.name}`}
                        disabled={pendingId === m.id}
                        className="rounded-md p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500 disabled:opacity-50"
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          void handleDelete(m);
                        }}
                        aria-label={`Remove ${m.name}`}
                        disabled={pendingId === m.id}
                        className="rounded-md p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-danger)]/10 hover:text-[color:var(--color-danger)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500 disabled:opacity-50"
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

// ---------------------------------------------------------------------------
// Member drawer (create/edit) — uses a local form schema. The validation
// package ships householdMemberCreateSchema but it requires `householdId`,
// which we inject server-side from the URL parameter.
// ---------------------------------------------------------------------------

type MemberDrawerState =
  | { kind: 'create' }
  | { kind: 'edit'; member: HouseholdMember }
  | null;

const memberFormSchema = z
  .object({
    name: z.string().trim().min(2, 'Name is required').max(120),
    relation: z.string().trim().min(2, 'Relation is required').max(40),
    phone: z.string().trim().min(5, 'Phone is required').max(40),
    email: z.string().trim().email('Invalid email').optional().or(z.literal('')),
  })
  .strict();

type MemberFormValues = z.infer<typeof memberFormSchema>;

interface MemberDrawerProps {
  state: MemberDrawerState;
  householdId: string;
  onClose: () => void;
  onSaved: () => void;
}

function MemberDrawer({
  state,
  householdId,
  onClose,
  onSaved,
}: MemberDrawerProps): JSX.Element {
  const { toast } = useToast();
  const isEdit = state?.kind === 'edit';

  const defaults = useMemo<MemberFormValues>(() => {
    if (state?.kind === 'edit') {
      const m = state.member;
      return {
        name: m.name,
        relation: m.relation,
        phone: m.phone,
        email: m.email ?? '',
      };
    }
    return { name: '', relation: '', phone: '', email: '' };
  }, [state]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MemberFormValues>({
    resolver: zodResolver(memberFormSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (state) reset(defaults);
  }, [state, defaults, reset]);

  const submit = handleSubmit(async (values) => {
    try {
      const body: Record<string, unknown> = {
        name: values.name,
        relation: values.relation,
        phone: values.phone,
      };
      const trimmedEmail = values.email?.trim();
      if (trimmedEmail) body.email = trimmedEmail;

      if (state?.kind === 'edit') {
        await apiRequest<{ ok: true }>(
          `/api/households/${encodeURIComponent(householdId)}/members/${encodeURIComponent(state.member.id)}`,
          { method: 'PATCH', body },
        );
        toast('Member updated.', { variant: 'success' });
      } else {
        await apiRequest<{ id: string }>(
          `/api/households/${encodeURIComponent(householdId)}/members`,
          { method: 'POST', body: { ...body, householdId } },
        );
        toast('Member added.', { variant: 'success' });
      }
      onSaved();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not save member.', {
        variant: 'error',
      });
    }
  });

  return (
    <Drawer
      open={state !== null}
      onClose={onClose}
      title={isEdit ? 'Edit family member' : 'Add family member'}
      size="md"
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        <TextField
          label="Full name"
          autoComplete="name"
          error={errors.name?.message}
          {...register('name')}
        />
        <TextField
          label="Relation"
          placeholder="e.g. Mother, Father, Uncle"
          error={errors.relation?.message}
          {...register('relation')}
        />
        <TextField
          label="Phone"
          type="tel"
          autoComplete="tel"
          error={errors.phone?.message}
          {...register('phone')}
        />
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          optional
          error={errors.email?.message}
          {...register('email')}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            {isEdit ? 'Save changes' : 'Add member'}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Parent account tab
// ---------------------------------------------------------------------------

interface ParentTabProps {
  household: HouseholdDetail;
  canManage: boolean;
  reinvitePending: boolean;
  onResend: () => void;
}

function ParentTab({
  household,
  canManage,
  reinvitePending,
  onResend,
}: ParentTabProps): JSX.Element {
  const user = household.parentUser ?? null;

  if (!user) {
    return (
      <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Parent account
        </h3>
        <p className="mt-3 text-sm text-[color:var(--color-text-muted)]">
          No parent account created yet.
        </p>
        {canManage ? (
          <div className="mt-3">
            <Link
              to="/system/users"
              className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-1.5 text-sm font-medium text-[color:var(--color-text)] hover:border-gold-500 hover:text-gold-700"
            >
              <UserPlus size={14} aria-hidden="true" />
              Send invitation
            </Link>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        Parent account
      </h3>
      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        <DetailRow label="Display name" value={user.displayName} />
        <DetailRow label="Email" value={user.email} />
        <DetailRow
          label="Status"
          value={<PortalStatusChip user={user} />}
        />
        <DetailRow
          label="Last login"
          value={
            user.lastLoginAt
              ? formatDate(user.lastLoginAt, { withTime: true })
              : '—'
          }
        />
      </dl>
      <div className="mt-4 flex flex-wrap gap-2">
        {canManage && user.status === 'pending' ? (
          <Button
            size="sm"
            variant="secondary"
            loading={reinvitePending}
            onClick={onResend}
          >
            Resend activation
          </Button>
        ) : null}
        {canManage ? (
          <Link
            to="/system/users"
            className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 py-1.5 text-sm font-medium text-[color:var(--color-text)] hover:border-gold-500 hover:text-gold-700"
          >
            Manage in Logins & roles
          </Link>
        ) : null}
      </div>
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[color:var(--color-border)] pb-2 last:border-b-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="text-sm text-[color:var(--color-text)]">{value}</dd>
    </div>
  );
}
